import { memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useRillContext } from "../../../../../rill/RillReact/share/base";
import type { ClientConfiguration } from "../../../../../types/clientConfig";
import type { Athlete } from "../../../../../types/user";
import { resolveAthleteClientConfiguration } from "../../../../../utils/client-config-from-tasks";
import { isActiveUserTheCoach } from "../../../../../utils/user-helpers";
import type { ClientConfigWidgetAvailability } from "../../../../athlete-portal/profile/components/ClientConfiguration/utils";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip";
import { AthleteRow } from "./AthleteRow";
import { useVisibleUnloadedRows } from "./useVisibleUnloadedRows";

export const ConfigsTable = memo(
  ({
    athletes,
    configsByClientId,
    referAsClients,
    clientConfigWidgetAvailability,
    blockedSections,
    onVisibleUnloadedIds,
  }: {
    athletes: Athlete[];
    configsByClientId: Record<string, ClientConfiguration>;
    referAsClients: boolean;
    clientConfigWidgetAvailability?: ClientConfigWidgetAvailability;
    blockedSections?: Record<string, Record<string, boolean>> | null;
    // When the server only sent the first page, fetch configs for unloaded
    // rows that are actually on screen — see AthletesConfigList.tsx.
    onVisibleUnloadedIds?: (clientIds: string[]) => void;
  }) => {
    const { t } = useTranslation(["athlete-portal", "coaching-portal"]);
    const { locals } = useRillContext();
    const tableWrapRef = useRef<HTMLDivElement>(null);
    const unloadedRowSignature = onVisibleUnloadedIds
      ? athletes
          .filter((athlete) => !configsByClientId?.[String(athlete._id)])
          .map((athlete) => String(athlete._id))
          .join(",")
      : "";

    useVisibleUnloadedRows(
      tableWrapRef,
      onVisibleUnloadedIds,
      unloadedRowSignature
    );

    return (
      <div className="athletes-config-list__table-wrap" ref={tableWrapRef}>
        <table className="athletes-config-list__table">
          <thead>
            <tr>
              <th scope="col">
                {referAsClients
                  ? t("coaching-portal:athletes.roleClient")
                  : t("coaching-portal:athletes.roleAthlete")}
              </th>
              <th scope="col">
                {t("athlete-portal:clientConfiguration.configList.template")}
              </th>
              <th scope="col">
                {t("athlete-portal:clientConfiguration.configList.training")}
              </th>
              <th scope="col">
                {t("athlete-portal:clientConfiguration.configList.nutrition")}
              </th>
              <th scope="col">
                {t("athlete-portal:clientConfiguration.configList.mealPlans")}
              </th>
              <th scope="col">
                {t(
                  "athlete-portal:clientConfiguration.configList.accountabilities"
                )}
              </th>
              <th scope="col">
                {t("athlete-portal:clientConfiguration.configList.goals")}
              </th>
              <th scope="col">
                {t("athlete-portal:clientConfiguration.configList.tracking")}
              </th>
              <th scope="col">
                <span className="athletes-config-list__column-header-with-tooltip">
                  {t("athlete-portal:clientConfiguration.configList.cycle")}
                  <ToolTip
                    id="cycle-column-tooltip"
                    body={t(
                      "athlete-portal:clientConfiguration.configList.cycleColumnTooltip"
                    )}
                  >
                    <SvgIcon
                      classNames="athletes-config-list__column-header-tooltip-icon"
                      style={{}}
                      alt={t(
                        "athlete-portal:clientConfiguration.configList.cycleColumnTooltip"
                      )}
                      src="/img/new-icons/question-circle.svg"
                    />
                  </ToolTip>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {athletes.map((athlete) => {
              const athleteConfig = resolveAthleteClientConfiguration(
                athlete,
                configsByClientId
              );
              const configSections = athleteConfig?.sections ?? {};
              const { activeUser } = locals;
              const isLoading = !athleteConfig;

              const athleteIsFromOtherOrganizationCoach =
                activeUser.organizationId &&
                athlete.coachId &&
                !isActiveUserTheCoach(activeUser._id, athlete.coachId)
                  ? athlete.coachId
                  : undefined;

              const skeleton = <div className="skeleton-cell" />;

              return (
                <AthleteRow
                  key={athlete._id}
                  athlete={athlete}
                  athleteConfig={athleteConfig}
                  configSections={configSections}
                  isLoading={isLoading}
                  athleteIsFromOtherOrganizationCoach={
                    athleteIsFromOtherOrganizationCoach
                  }
                  skeleton={skeleton}
                  clientConfigWidgetAvailability={
                    clientConfigWidgetAvailability
                  }
                  sectionHasData={blockedSections?.[athlete?._id] ?? null}
                />
              );
            })}
          </tbody>
        </table>
        <ReactTooltip id="tracking-emoji-tooltip" place="top" />
        <ReactTooltip id="widget-warning-tooltip" place="top" />
      </div>
    );
  }
);
